import { useEffect, useMemo, useState } from 'react';
import { Eye, Flag, Home, Lightbulb, Plus, RotateCcw, Undo2, Users } from 'lucide-react';
import type { Color } from '../../shared/protocol.ts';
import { useAuth } from '../app/auth.ts';
import { usePreferences } from '../app/preferences.ts';
import { navigate } from '../app/router.ts';
import { Board } from '../components/Board.tsx';
import { BotAvatar, PersonAvatar } from '../components/Brand.tsx';
import { ConfirmDialog } from '../components/Dialog.tsx';
import { GameLayout } from '../components/GameLayout.tsx';
import { Link } from '../components/Link.tsx';
import { MoveList } from '../components/MoveList.tsx';
import { PlayerBar, ThinkingDots, YourTurnBadge } from '../components/PlayerBar.tsx';
import { ResultDialog } from '../components/ResultDialog.tsx';
import { MovesFold, TurnBanner, type TurnKind } from '../components/TurnBanner.tsx';
import { loadSavedComputerGame, useComputerGame } from '../hooks/useComputerGame.ts';
import { useHistoryCursor, useResultDialog } from '../hooks/useGameView.ts';
import { materialSummary, positionsAfter } from '../lib/chess.ts';

export function ComputerGamePage() {
  const [saved] = useState(loadSavedComputerGame);

  useEffect(() => {
    if (!saved) navigate('/computer', { replace: true });
  }, [saved]);

  return saved ? <ComputerGame initial={saved} /> : null;
}

function ComputerGame({ initial }: { initial: NonNullable<ReturnType<typeof loadSavedComputerGame>> }) {
  const { prefs, t } = usePreferences();
  const { user } = useAuth();
  const game = useComputerGame(initial);
  const { config, bot, moves, result } = game;

  const [flipped, setFlipped] = useState(false);
  const orientation: Color = flipped ? botColorOf(config.color) : config.color;
  const [confirmResign, setConfirmResign] = useState(false);

  const fens = useMemo(() => positionsAfter(moves), [moves]);
  const livePly = moves.length;
  const history = useHistoryCursor(livePly);
  const { ply, isLive } = history;
  const shownFen = fens[ply];
  const shownLast = ply > 0 ? moves[ply - 1] : null;

  const resultDialog = useResultDialog(result ? `${game.gameId}:${livePly}:${result.reason}` : null);

  const material = useMemo(() => materialSummary(shownFen), [shownFen]);
  const botColor = botColorOf(config.color);
  // Signed-in players appear with their account name and look; guests with their nickname.
  const account = user?.username ? user : null;
  const ownName = account?.username ?? prefs.playerName.trim();
  const playerName = ownName ? `${ownName} (${t('game.you')})` : t('game.you');
  const botName = t(`level.${bot.level}`);

  const bar = (color: Color) =>
    color === botColor ? (
      <PlayerBar
        name={botName}
        avatar={<BotAvatar bot={bot} />}
        color={botColor}
        captured={material.captured[botColor]}
        lead={material.lead[botColor]}
        status={game.thinking ? <ThinkingDots label={t('game.thinking')} /> : null}
      />
    ) : (
      <PlayerBar
        name={playerName}
        avatar={<PersonAvatar name={ownName} />}
        {...(account ? { look: account.look } : {})}
        color={config.color}
        captured={material.captured[config.color]}
        lead={material.lead[config.color]}
        status={
          isLive && playing && myTurn && !game.thinking ? (
            <YourTurnBadge label={turnKind === 'check' ? t('game.check') : t('game.yourTurn')} check={turnKind === 'check'} />
          ) : null
        }
      />
    );

  const turn: Color = livePly % 2 === 0 ? 'w' : 'b';
  const myTurn = turn === config.color;
  const [turnKind, turnText]: [TurnKind, string] = result
    ? ['over', t(`reason.${result.reason}`)]
    : !myTurn || game.thinking
      ? ['wait', t('game.botTurn', { name: t(`level.${bot.level}`) })]
      : game.chess.inCheck()
        ? ['check', t('game.checkYou')]
        : ['mine', t('game.yourTurn')];

  const playing = result === null;
  const newGame = () => navigate('/computer');

  return (
    <>
      <GameLayout
        turn={isLive ? turnKind : undefined}
        top={bar(orientation === 'w' ? 'b' : 'w')}
        bottom={bar(orientation)}
        board={
          <Board
            fen={shownFen}
            orientation={orientation}
            movableColor={isLive && playing && !game.thinking ? config.color : null}
            lastMove={shownLast}
            hint={isLive ? game.hint : null}
            historical={!isLive}
            onMove={game.playerMove}
          />
        }
        panel={
          <>
            <div className="panel-section turn-section">
              <TurnBanner kind={turnKind} text={turnText} />
            </div>
            {!isLive && (
              <div className="panel-section panel-status">
                <div className="banner">
                  <span>{t('game.viewing')}</span>
                  <button type="button" className="btn btn-sm btn-blue" onClick={history.backToLive}>
                    {t('game.backToLive')}
                  </button>
                </div>
              </div>
            )}
            <div className="panel-section panel-status game-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={game.requestHint}
                disabled={!playing || game.thinking || !myTurn}
              >
                <Lightbulb size={24} /> {t('game.hint')}
              </button>
              <button type="button" className="btn btn-blue" onClick={game.undo} disabled={!game.canUndo}>
                <Undo2 size={24} /> {t('game.undo')}
              </button>
              {playing ? (
                <button type="button" className="btn" onClick={() => setConfirmResign(true)} disabled={livePly < 1}>
                  <Flag size={22} /> {t('game.resign')}
                </button>
              ) : (
                <button type="button" className="btn btn-green" onClick={() => game.restart()}>
                  <RotateCcw size={22} /> {t('game.rematch')}
                </button>
              )}
              <button type="button" className="btn" onClick={newGame}>
                <Plus size={22} /> {t('game.newGame')}
              </button>
            </div>
            <MovesFold>
              <MoveList
                sans={moves.map((m) => m.san)}
                ply={ply}
                onSelect={history.select}
                onFlip={() => setFlipped((f) => !f)}
              />
            </MovesFold>
          </>
        }
      />

      <ConfirmDialog
        open={confirmResign}
        title={t('confirm.resign.title')}
        body={t('confirm.resign.body')}
        confirmLabel={t('game.resign')}
        danger
        onCancel={() => setConfirmResign(false)}
        onConfirm={() => {
          setConfirmResign(false);
          game.resign();
        }}
      />

      <ResultDialog
        open={resultDialog.open}
        result={result}
        perspective={config.color}
        moveCount={livePly}
        players={config.color === 'w' ? { w: playerName, b: botName } : { w: botName, b: playerName }}
        onClose={resultDialog.close}
        actions={
          <>
            <button type="button" className="btn btn-green" onClick={() => game.restart()} autoFocus>
              <RotateCcw size={22} /> {t('game.rematch')}
            </button>
            <Link href="/computer" className="btn">
              <Users size={22} /> {t('computer.otherOpponent')}
            </Link>
            <Link href="/" className="btn">
              <Home size={22} /> {t('result.menu')}
            </Link>
            <button type="button" className="btn btn-ghost btn-sm" onClick={resultDialog.close}>
              <Eye size={20} /> {t('result.review')}
            </button>
          </>
        }
      />
    </>
  );
}

const botColorOf = (player: Color): Color => (player === 'w' ? 'b' : 'w');
